/* ==========================================================================
   Pawro — Hero → Featured → "Why Pawro" story motion (GSAP + ScrollTrigger)

   Design goals:
   - Native scroll stays intact (no smooth-scroll engine, no scroll traps).
   - Transform/opacity only. Few ScrollTriggers.
   - GSAP only targets wrapper elements that have NO CSS keyframe animation,
     so it never fights the CSS float/breathe/drift loops.
   - Every entrance is BIDIRECTIONAL: it plays when its scene scrolls in and
     reverses smoothly when the scene scrolls back out above the start line
     (onEnter/onLeaveBack or toggleActions — never `once: true`). Timelines
     are created a single time and toggled; nothing is rebuilt per scroll.
   - Scene 3 ("Why Pawro"):
       * Hidden initial states are applied ONLY here, after GSAP +
         ScrollTrigger are confirmed loaded — with slow/failed JS the section
         renders fully visible via plain CSS.
       * The reveal is a time-based entrance (not scrubbed), so content can
         never get stuck at opacity 0.
       * NO PIN. The old short pin (start 'top top', +=55%) read as a scroll
         snag — the page visibly "stuck" for half a viewport, and pinning
         inside the `.page-wrapper` scroller uses pinType:transform, which
         drags behind native wheel scrolling. The same depth beat is now a
         plain scrub across the scene's passage, so scrolling is continuous
         in both directions.
   - Scene 4 ("Join the family" newsletter):
       * Same safety model as Scene 3: hidden states only from JS, time-based
         entrance (never scrubbed), fully visible without JS.
       * No pin. The only scrubbed motion is a gentle depth drift on the
         scene photo that completes by mid-scene, so all scroll-linked motion
         has settled before the visitor reaches the footer.
   - Duplicate-init safe: a global script guard, a per-section data flag, and
     a full matchMedia revert + rebuild on shopify:section:load (kills stale
     triggers if the DOM is replaced).
   - ScrollTrigger.refresh() runs only once layout is stable: after build,
     after the lazy story photo reports real dimensions, and on window load.
   - Respects prefers-reduced-motion (no triggers are created, nothing is
     hidden and all content shows immediately).

   Note: on desktop (>=990px) this theme scrolls inside `.page-wrapper`
   (html/body are overflow:hidden); on mobile the window scrolls. ScrollTrigger
   is pointed at the correct scroller per breakpoint.
   ========================================================================== */
(function () {
  if (window.__pawroMotionInit) return;
  window.__pawroMotionInit = true;

  var mm = null;

  function build() {
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    if (!gsap || !ScrollTrigger) return;

    if (!gsap.core.globals().ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
    }

    /* Rebuild from scratch: reverting the previous matchMedia context kills
       any stale triggers left behind by a replaced section DOM. */
    if (mm) mm.revert();
    mm = gsap.matchMedia();

    mm.add(
      {
        isDesktop: '(min-width: 990px) and (prefers-reduced-motion: no-preference)',
        isMobile: '(max-width: 989px) and (prefers-reduced-motion: no-preference)'
      },
      function (ctx) {
        var d = ctx.conditions;
        /* Desktop scroll happens on .page-wrapper; mobile scrolls the window. */
        var scroller = d.isDesktop ? '.page-wrapper' : undefined;

        /* Bidirectional entrance binder: plays going down, reverses going
           back up. `onRefresh` is the safety net — lazy images/layout
           changes move trigger positions mid-flight, and a recalculated
           start can flip a trigger's state WITHOUT firing the matching
           callback, stranding content hidden while on screen. After every
           refresh we hard-sync: past the start line => fully shown, above
           it => hidden (mid-animation states are left alone). One trigger
           per animation; instances are never recreated on scroll. */
        function bindEnter(triggerEl, anim, startPos) {
          ScrollTrigger.create({
            trigger: triggerEl,
            scroller: scroller,
            start: startPos,
            onEnter: function () { anim.play(); },
            onLeaveBack: function () { anim.reverse(); },
            onRefresh: function (self) {
              if (anim.isActive()) return;
              if (self.progress > 0) anim.progress(1);
              else if (anim.progress() > 0) anim.progress(0);
            }
          });
        }

        /* ---- Featured Products entrance (once) ------------------------- */
        var featured = document.querySelector('.pawro-featured');
        if (featured) {
          var header = featured.querySelector('.pawro-featured__header');
          if (header) {
            var headerTween = gsap.from(header.children, {
              opacity: 0,
              y: 22,
              duration: 0.6,
              ease: 'power2.out',
              stagger: 0.08,
              paused: true
            });
            bindEnter(featured, headerTween, 'top 82%');
          }

          var cards = featured.querySelectorAll('.pawro-card');
          if (cards.length) {
            var cardsTween = gsap.from(cards, {
              opacity: 0,
              y: 34,
              scale: 0.96,
              duration: 0.62,
              ease: 'power3.out',
              stagger: 0.09,
              paused: true,
              /* land in the exact approved layout so hover + carousel stay clean */
              clearProps: 'transform,opacity'
            });
            bindEnter(featured, cardsTween, 'top 74%');
          }
        }

        /* ---- Hero depth parallax (subtle, scrubbed, transform-only) ----- */
        var hero = document.querySelector('.pawro-hero');
        if (hero) {
          var scene = hero.querySelector('.pawro-hero__scene');
          var text = hero.querySelector('.pawro-hero__text');
          var sShift = d.isDesktop ? 8 : 4; /* reduced parallax on mobile */
          var tShift = d.isDesktop ? -6 : -3;

          if (scene) {
            gsap.to(scene, {
              yPercent: sShift,
              ease: 'none',
              scrollTrigger: { trigger: hero, scroller: scroller, start: 'top top', end: 'bottom top', scrub: 0.5 }
            });
          }
          if (text) {
            gsap.to(text, {
              yPercent: tShift,
              ease: 'none',
              scrollTrigger: { trigger: hero, scroller: scroller, start: 'top top', end: 'bottom top', scrub: 0.5 }
            });
          }
        }

        /* ---- Scene 3: "Why Pawro" scroll story ------------------------- */
        var story = document.querySelector('.pawro-story');
        if (story && story.dataset.pfStoryInit !== '1') {
          story.dataset.pfStoryInit = '1'; /* duplicate-timeline guard */

          /* Pin the section's own Shopify wrapper so the pin-spacer sits
             cleanly between sections in the page flow. */
          var wrapper = story.closest('.shopify-section') || story;

          var eyebrow = story.querySelector('.pawro-story__eyebrow');
          var lines = story.querySelectorAll('.ps-line');
          var rule = story.querySelector('.pawro-story__accent-rule');
          var para = story.querySelector('.pawro-story__para');
          var cta = story.querySelector('.pawro-story__cta');
          var imgWrap = story.querySelector('.pawro-story__image-wrap');
          var textCol = story.querySelector('.pawro-story__text');
          var stage = story.querySelector('.pawro-story__stage');
          var storyCards = story.querySelectorAll('.pawro-story__card');
          var storyValues = story.querySelectorAll('.pawro-story__value');
          var handoff = story.querySelector('[data-ps-handoff]');

          if (d.isDesktop) {
            /* 1) Initial states — applied only now that GSAP + ScrollTrigger
               are ready, so a slow script never leaves the scene hidden. */
            var fadeUps = [eyebrow, para, cta].filter(Boolean);
            if (fadeUps.length) gsap.set(fadeUps, { autoAlpha: 0, y: 18 });
            if (imgWrap) gsap.set(imgWrap, { autoAlpha: 0, scale: 1.05, yPercent: 3 });
            if (lines.length) gsap.set(lines, { yPercent: 118 });
            if (rule) gsap.set(rule, { scaleX: 0, transformOrigin: 'left center' });
            if (storyCards.length) gsap.set(storyCards, { autoAlpha: 0, y: 26, scale: 0.96 });
            if (storyValues.length) gsap.set(storyValues, { autoAlpha: 0, y: 22 });

            /* 2) Entrance reveal — time-based (NOT scrubbed), so it always
               runs to completion and nothing can be left half-hidden. It
               fires as the scene scrolls into view, well before the pin. */
            var allRevealed = fadeUps
              .concat(imgWrap ? [imgWrap] : [])
              .concat(rule ? [rule] : []);
            var enter = gsap.timeline({
              paused: true,
              onComplete: function () {
                /* land in the exact approved layout: hover, carousel and the
                   pure-CSS states take over from here */
                gsap.set(allRevealed, { clearProps: 'all' });
                if (lines.length) gsap.set(lines, { clearProps: 'all' });
                if (storyCards.length) gsap.set(storyCards, { clearProps: 'all' });
                if (storyValues.length) gsap.set(storyValues, { clearProps: 'all' });
              }
            });
            if (imgWrap) enter.to(imgWrap, { autoAlpha: 1, scale: 1, yPercent: 0, duration: 0.8, ease: 'power2.out' }, 0);
            if (eyebrow) enter.to(eyebrow, { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out' }, 0.1);
            if (lines.length) enter.to(lines, { yPercent: 0, duration: 0.7, ease: 'power3.out', stagger: 0.12 }, 0.18);
            if (rule) enter.to(rule, { scaleX: 1, duration: 0.4, ease: 'power2.out' }, 0.62);
            if (para) enter.to(para, { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out' }, 0.68);
            if (cta) enter.to(cta, { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out' }, 0.8);
            if (storyCards.length) enter.to(storyCards, { autoAlpha: 1, y: 0, scale: 1, duration: 0.55, ease: 'power3.out', stagger: 0.12 }, 0.55);
            if (storyValues.length) enter.to(storyValues, { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out', stagger: 0.07 }, 0.95);

            bindEnter(wrapper, enter, 'top 75%');

            /* 3) Depth drift — the same subtle beat the old pin provided,
               but as a plain scrub across the scene's passage: no pin, no
               pause, fully reversible, and native scrolling stays smooth. */
            var driftTl = gsap.timeline();
            if (textCol) driftTl.fromTo(textCol, { yPercent: 1.5 }, { yPercent: -3, ease: 'none' }, 0);
            if (stage) driftTl.fromTo(stage, { yPercent: 3 }, { yPercent: -6, ease: 'none' }, 0);

            ScrollTrigger.create({
              trigger: wrapper,
              scroller: scroller,
              start: 'top 60%',
              end: 'bottom top',
              scrub: 0.6,
              animation: driftTl,
              invalidateOnRefresh: true
            });
          } else {
            /* Mobile: no pin — a light progressive entrance, native scroll. */
            var revealTargets = story.querySelectorAll(
              '.pawro-story__eyebrow, .ps-line, .pawro-story__para, .pawro-story__cta, .pawro-story__image-wrap, .pawro-story__card, .pawro-story__value'
            );
            var storyReveal = gsap.from(revealTargets, {
              autoAlpha: 0,
              y: 22,
              duration: 0.5,
              ease: 'power2.out',
              stagger: 0.05,
              paused: true,
              clearProps: 'transform,opacity,visibility'
            });
            bindEnter(story, storyReveal, 'top 80%');
          }

          /* A heart travels down through the scene, linking Scene 2 -> Scene 3. */
          if (handoff) {
            gsap.to(handoff, {
              yPercent: d.isDesktop ? 620 : 360,
              rotation: 12,
              ease: 'none',
              scrollTrigger: { trigger: wrapper, scroller: scroller, start: 'top bottom', end: 'bottom top', scrub: 0.8 }
            });
          }
        }

        /* ---- Scene 4: "Join the family" newsletter CTA ------------------ */
        var join = document.querySelector('.pawro-join');
        if (join && join.dataset.pfJoinInit !== '1') {
          join.dataset.pfJoinInit = '1'; /* duplicate-timeline guard */

          var jWrapper = join.closest('.shopify-section') || join;
          var jEyebrow = join.querySelector('.pawro-join__eyebrow');
          var jLines = join.querySelectorAll('.pj-line');
          var jPara = join.querySelector('.pawro-join__para');
          var jForm = join.querySelector('.pawro-join__form-wrap');
          var jBenefits = join.querySelectorAll('.pawro-join__benefit');
          var jStage = join.querySelector('.pawro-join__stage');
          var jImage = join.querySelector('.pawro-join__image-wrap');
          var jHandoff = join.querySelector('[data-pj-handoff]');

          if (d.isDesktop) {
            /* 1) Initial states — applied only now that GSAP is ready, so a
               slow script never leaves the invitation hidden. */
            var jFadeUps = [jEyebrow, jPara, jForm].filter(Boolean);
            if (jFadeUps.length) gsap.set(jFadeUps, { autoAlpha: 0, y: 16 });
            if (jLines.length) gsap.set(jLines, { yPercent: 118 });
            if (jStage) gsap.set(jStage, { autoAlpha: 0, scale: 1.04, xPercent: 3 });
            if (jBenefits.length) gsap.set(jBenefits, { autoAlpha: 0, y: 18 });

            /* 2) Entrance reveal — time-based (NOT scrubbed): a soft, staggered
               welcome that always runs to completion. */
            var jEnter = gsap.timeline({
              paused: true,
              onComplete: function () {
                /* hand the approved layout back to pure CSS (hover states,
                   focus rings, ambient float loops) */
                gsap.set(jFadeUps, { clearProps: 'all' });
                if (jLines.length) gsap.set(jLines, { clearProps: 'all' });
                if (jStage) gsap.set(jStage, { clearProps: 'all' });
                if (jBenefits.length) gsap.set(jBenefits, { clearProps: 'all' });
              }
            });
            if (jStage) jEnter.to(jStage, { autoAlpha: 1, scale: 1, xPercent: 0, duration: 0.9, ease: 'power2.out' }, 0);
            if (jEyebrow) jEnter.to(jEyebrow, { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out' }, 0.08);
            if (jLines.length) jEnter.to(jLines, { yPercent: 0, duration: 0.7, ease: 'power3.out', stagger: 0.12 }, 0.16);
            if (jPara) jEnter.to(jPara, { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out' }, 0.52);
            if (jForm) jEnter.to(jForm, { autoAlpha: 1, y: 0, duration: 0.55, ease: 'power2.out' }, 0.66);
            if (jBenefits.length) jEnter.to(jBenefits, { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out', stagger: 0.08 }, 0.82);

            bindEnter(jWrapper, jEnter, 'top 78%');

            /* 3) Depth drift on the photo only — scrubbed, but finished by the
               time the scene centers, so the ending is calm before the footer. */
            if (jImage) {
              gsap.fromTo(
                jImage,
                { yPercent: 5 },
                {
                  yPercent: 0,
                  ease: 'none',
                  scrollTrigger: {
                    trigger: jWrapper,
                    scroller: scroller,
                    start: 'top bottom',
                    end: 'center 55%',
                    scrub: 0.6,
                    invalidateOnRefresh: true
                  }
                }
              );
            }
          } else {
            /* Mobile: no parallax — one light progressive entrance. */
            var jRevealTargets = join.querySelectorAll(
              '.pawro-join__eyebrow, .pj-line, .pawro-join__para, .pawro-join__form-wrap, .pawro-join__stage, .pawro-join__benefit'
            );
            var joinReveal = gsap.from(jRevealTargets, {
              autoAlpha: 0,
              y: 20,
              duration: 0.5,
              ease: 'power2.out',
              stagger: 0.05,
              paused: true,
              clearProps: 'transform,opacity,visibility'
            });
            bindEnter(join, joinReveal, 'top 82%');
          }

          /* A heart drifts down into the scene — the same thread that has
             linked every Pawro scene so far. Ends early so the scene settles. */
          if (jHandoff) {
            gsap.to(jHandoff, {
              yPercent: d.isDesktop ? 420 : 240,
              rotation: 10,
              ease: 'none',
              scrollTrigger: { trigger: jWrapper, scroller: scroller, start: 'top bottom', end: 'center 40%', scrub: 0.8 }
            });
          }
        }

        /* Allow a clean re-init when this context is reverted (breakpoint
           change or shopify:section:load rebuild). */
        return function () {
          if (story) delete story.dataset.pfStoryInit;
          if (join) delete join.dataset.pfJoinInit;
        };
      }
    );

    /* Refresh only after the lazy scene photos have real dimensions. */
    ['.pawro-story__image', '.pawro-join__image'].forEach(function (sel) {
      var img = document.querySelector(sel);
      if (img && !img.complete) {
        img.addEventListener('load', function () { ScrollTrigger.refresh(); }, { once: true });
      }
    });
  }

  function init() {
    if (!window.gsap || !window.ScrollTrigger) return;
    build();

    /* Recalculate once every eager asset has settled. */
    window.addEventListener('load', function () {
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }, { once: true });

    /* Theme editor / dev reload replaces section DOM: rebuild everything so
       no trigger points at a detached node. */
    document.addEventListener('shopify:section:load', function () { build(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
